import { useState, useRef, useEffect, useCallback } from 'react';

export function usePanZoom(base) {
  const key = base.map((v) => Math.round(v)).join(',');
  const [vb, setVb] = useState(base);
  const ptrs = useRef(new Map());
  const last = useRef(null);
  const moved = useRef(false);
  const start = useRef([0, 0]);
  const svg = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setVb(base); }, [key]);

  const toUnits = (dxPx, v) => {
    const r = svg.current?.getBoundingClientRect();
    if (!r || !r.width) return 0;
    return dxPx * Math.max(v[2] / r.width, v[3] / r.height);
  };

  const zoomAt = useCallback((factor, cx, cy) => {
    setVb((v) => {
      const nw = Math.min(base[2] * 3, Math.max(base[2] * 0.15, v[2] * factor));
      const f = nw / v[2];
      const px = cx ?? v[0] + v[2] / 2, py = cy ?? v[1] + v[3] / 2;
      return [px - (px - v[0]) * f, py - (py - v[1]) * f, v[2] * f, v[3] * f];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const clientToSvg = (x, y, v) => {
    const r = svg.current.getBoundingClientRect();
    const sc = Math.max(v[2] / r.width, v[3] / r.height);
    const ox = v[0] + v[2] / 2 - (r.width * sc) / 2, oy = v[1] + v[3] / 2 - (r.height * sc) / 2;
    return [ox + (x - r.left) * sc, oy + (y - r.top) * sc];
  };

  useEffect(() => {
    const el = svg.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setVb((v) => {
        const [cx, cy] = clientToSvg(e.clientX, e.clientY, v);
        const f = e.deltaY > 0 ? 1.12 : 0.89;
        const nw = Math.min(base[2] * 3, Math.max(base[2] * 0.15, v[2] * f));
        const k = nw / v[2];
        return [cx - (cx - v[0]) * k, cy - (cy - v[1]) * k, v[2] * k, v[3] * k];
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const bind = {
    onPointerDown: (e) => {
      ptrs.current.set(e.pointerId, [e.clientX, e.clientY]);
      moved.current = false;
      start.current = [e.clientX, e.clientY];
      last.current = null;
    },
    onPointerMove: (e) => {
      if (!ptrs.current.has(e.pointerId)) return;
      const prev = ptrs.current.get(e.pointerId);
      ptrs.current.set(e.pointerId, [e.clientX, e.clientY]);
      const list = [...ptrs.current.values()];
      if (list.length === 1) {
        const dx = e.clientX - prev[0], dy = e.clientY - prev[1];
        if (Math.hypot(e.clientX - start.current[0], e.clientY - start.current[1]) > 6) moved.current = true;
        setVb((v) => [v[0] - toUnits(dx, v), v[1] - toUnits(dy, v), v[2], v[3]]);
      } else if (list.length === 2) {
        moved.current = true;
        const dist = Math.hypot(list[0][0] - list[1][0], list[0][1] - list[1][1]);
        if (last.current) {
          const f = last.current / dist;
          setVb((v) => {
            const [cx, cy] = clientToSvg((list[0][0] + list[1][0]) / 2, (list[0][1] + list[1][1]) / 2, v);
            const nw = Math.min(base[2] * 3, Math.max(base[2] * 0.15, v[2] * f));
            const k = nw / v[2];
            return [cx - (cx - v[0]) * k, cy - (cy - v[1]) * k, v[2] * k, v[3] * k];
          });
        }
        last.current = dist;
      }
    },
    onPointerUp: (e) => { ptrs.current.delete(e.pointerId); last.current = null; },
    onPointerLeave: (e) => { ptrs.current.delete(e.pointerId); last.current = null; },
    onPointerCancel: (e) => { ptrs.current.delete(e.pointerId); last.current = null; },
    onClickCapture: (e) => { if (moved.current) { e.stopPropagation(); moved.current = false; } },
  };

  return {
    vb, svgRef: svg, bind,
    zoomIn: () => zoomAt(0.8), zoomOut: () => zoomAt(1.25), reset: () => setVb(base),
  };
}
